// src/components/Product/ContentCard.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { convertToPersianNumbers } from '../../utils/PersianNumberUtils';

const SAFE_FALLBACK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const TAILWIND_GRADIENTS = [
  'from-red-500', 'from-blue-500', 'from-green-500', 'from-purple-500',
  'from-pink-500', 'from-indigo-500', 'from-yellow-500', 'from-orange-500',
  'from-rose-500', 'from-violet-500', 'from-fuchsia-500', 'from-sky-500',
  'from-emerald-500', 'from-amber-500'
];

const RandomFallbackImage = ({ src: initialSrc, alt, className = '' }) => {
  const [displayUrl, setDisplayUrl] = useState(initialSrc || SAFE_FALLBACK);
  const [hasAttemptedFallback, setHasAttemptedFallback] = useState(!initialSrc);
  const randomGradientClass = useMemo(() => {
    const color = TAILWIND_GRADIENTS[Math.floor(Math.random() * TAILWIND_GRADIENTS.length)];
    return `absolute inset-0 bg-gradient-to-r ${color} to-transparent opacity-30 pointer-events-none`;
  }, []);

  useEffect(() => {
    if (initialSrc) {
      setDisplayUrl(initialSrc);
      setHasAttemptedFallback(false);
    }
  }, [initialSrc]);

  const handleImageError = useCallback(() => {
    if (!hasAttemptedFallback && initialSrc) {
      setDisplayUrl(SAFE_FALLBACK);
      setHasAttemptedFallback(true);
    }
  }, [hasAttemptedFallback, initialSrc]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      <img
        key={displayUrl}
        src={displayUrl}
        alt={alt || 'تصویر محصول'}
        className={`w-full h-full object-cover transition duration-200 group-hover:scale-[1.03] ${className}`}
        style={{ filter: 'grayscale(100%) contrast(150%)' }}
        onError={handleImageError}
        loading="lazy"
        aria-hidden={!alt}
      />
      <div className={randomGradientClass} />
      <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </div>
  );
};

const ContentCard = ({ data }) => {
  if (!data || (!data.slug && !data.productUrl)) return null;

  const {
    slug = '',
    title = 'بدون عنوان',
    price = null,
    is_free = false,
    price_display = '',
    is_new = false,
    productUrl = null,
    cover_url = null,
    category_slug = '',
    subcategory_slug = '',
  } = data;

  const finalProductLink = productUrl || (slug
    ? (category_slug
        ? (subcategory_slug
            ? `/products/${category_slug}/${subcategory_slug}/${slug}`
            : `/products/${category_slug}/${slug}`)
        : `/products/${slug}`)
    : '#');

  const isFreeContent = is_free === true || (price !== null && price <= 0);
  let displayPrice = 'تماس بگیرید';
  if (isFreeContent) {
    displayPrice = 'رایگان';
  } else if (price_display?.trim()) {
    displayPrice = convertToPersianNumbers(price_display);
  } else if (price !== null && price !== undefined) {
    displayPrice = convertToPersianNumbers(price);
  }

  const priceLabelBgClass = isFreeContent ? 'bg-cyan-700' : 'bg-indigo-700';
  const shouldShowPriceLabel = isFreeContent || price !== null || price_display?.trim();

  return (
    <Link
      to={finalProductLink}
      className="w-full relative block rtl text-right transition duration-200 group"
      title={title}
      aria-label={`مشاهده جزئیات ${title}`}
    >
      <div className="flex flex-col h-full bg-white rounded-lg overflow-hidden">
        {/* تصویر */}
        <div className="relative w-full overflow-hidden aspect-square">
          <RandomFallbackImage
            src={cover_url}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {is_new && (
            <span className="absolute top-2 left-2 bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded-md z-10">
              جدید
            </span>
          )}
          {shouldShowPriceLabel && (
            <span
              className={`absolute bottom-0 left-0 ${priceLabelBgClass} text-white text-[10px] font-light px-3 py-1 rounded-tr-lg z-10`}
            >
              {displayPrice}
            </span>
          )}
        </div>

        {/* عنوان با فاصله از تصویر و پایین */}
        <div className="pt-3 pb-4 px-2 flex flex-col flex-grow">
          <h3
            className="text-[8pt] font-regular text-gray-800 leading-snug overflow-hidden group-active:text-indigo-700 transition-colors duration-150 text-justify"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              textOverflow: 'clip',
              wordBreak: 'break-word'
            }}
          >
            {title}
          </h3>
        </div>
      </div>
    </Link>
  );
};

export default ContentCard;